import { useAuth } from '@/auth/Auth';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import LayoutOutside from '@/layout/LayoutOutside';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFetchAccountInvite } from './hooks/useFetchAccountInvite';
import { useAcceptAccountInvite } from './hooks/useAcceptAccountInvite';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEffect } from 'react';
import Loader from '@/components/Loader/Loader';
import { CheckCircle, Shield, Users } from 'lucide-react';

export function InvitationAcceptInvite() {
  const { token } = useParams<{ token: string }>();

  const { invite, isPending } = useFetchAccountInvite(token);
  const { acceptInvite, isAccepting } = useAcceptAccountInvite();

  const { t } = useTranslation('invitationAcceptInvite');
  const navigate = useNavigate();
  const location = useLocation();

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isPending || isAuthenticated) return;

    localStorage.setItem(
      'redirectAfterEmailVerification',
      `/invitation-accept/${token}`,
    );

    const destination = invite?.requires_password_setup ? '/register' : '/login';

    navigate(destination, {
      replace: true,
      state: {
        from: `/invitation-accept/${token}`,
        inviteEmail: invite?.email,
        inviteToken: token,
      },
    });
  }, [isPending, invite, isAuthenticated, token, navigate]);

  useEffect(() => {
    const redirectUrl = localStorage.getItem('redirectAfterEmailVerification');

    const handleBeforeUnload = () => {
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterEmailVerification');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (redirectUrl && location.pathname !== `/invitation-accept/${token}`) {
        localStorage.removeItem('redirectAfterEmailVerification');
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname, token]);

  const handleAcceptClick = () => {
    acceptInvite({ token: token!, email: invite?.email ?? '' });
  };

  const roleLabel = invite?.role
    ? t(`roles.${invite.role}`, { defaultValue: invite.role })
    : '';

  const permissionCount = invite?.permissions
    ? Object.keys(invite.permissions).length
    : 0;

  const isAlreadyAccepted = !!invite?.accepted_at;

  return (
    <LayoutOutside classNameMainContainer="mt-[10%]" hideButton>
      {isPending ? (
        <Loader fullScreen />
      ) : (
        <>
          {invite ? (
            <section className="max-w-md w-full flex flex-col items-center space-y-8">
              <div className="relative">
                <Avatar className="h-20 w-20 bg-blackA1">
                  <AvatarImage
                    src={invite.account?.logo_url ?? undefined}
                    className="w-full h-full rounded-full"
                    alt={`${invite.account?.name ?? ''} logo`}
                    loading="lazy"
                  />
                  <AvatarFallback className="bg-elevated text-xl font-medium text-muted">
                    {invite.account?.name?.[0] ?? '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                  <Users className="w-3.5 h-3.5 text-white" />
                </div>
              </div>

              <div className="space-y-1 text-center">
                <h4 className="font-medium text-muted-foreground">{t('invite_title')}</h4>
                <h1 className="text-3xl font-semibold">
                  {invite.account?.name ?? t('unknown_account')}
                </h1>
                <p className="text-sm text-muted-foreground mt-2">
                  {t('invited_by', { name: invite.inviter?.name ?? '' })}
                </p>
              </div>

              {isAlreadyAccepted ? (
                // Estado: convite já foi aceito anteriormente
                <div className="border w-full p-6 space-y-4 rounded text-center">
                  <CheckCircle className="w-10 h-10 mx-auto text-green-500" />
                  <p className="font-medium">{t('already_accepted')}</p>
                  <p className="text-sm text-muted-foreground">{t('already_accepted_description')}</p>
                  <Button className="w-full" onClick={() => navigate('/dashboard')}>
                    {t('go_to_dashboard')}
                  </Button>
                </div>
              ) : (
                <div className="border w-full p-4 space-y-6 rounded">
                  <Table className="w-full">
                    <TableBody>
                      <TableRow className="border-transparent">
                        <TableCell className="text-gray-400">
                          {t('account')}:
                        </TableCell>
                        <TableCell className="text-white">
                          {invite.account?.name ?? '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow className="border-transparent">
                        <TableCell className="text-gray-400">
                          {t('invited_by_label')}:
                        </TableCell>
                        <TableCell className="text-white">
                          {invite.inviter?.name ?? '-'}
                        </TableCell>
                      </TableRow>
                      {roleLabel ? (
                        <TableRow className="border-transparent">
                          <TableCell className="text-gray-400">
                            {t('role_label')}:
                          </TableCell>
                          <TableCell className="text-white">
                            <span className="inline-flex items-center gap-1.5">
                              <Shield className="w-3.5 h-3.5" />
                              {roleLabel}
                            </span>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {permissionCount > 0 ? (
                        <TableRow className="border-transparent">
                          <TableCell className="text-gray-400">
                            {t('permissions_label')}:
                          </TableCell>
                          <TableCell className="text-white">
                            {t('permissions_count', { count: permissionCount })}
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>

                  <Button
                    className="w-full"
                    onClick={handleAcceptClick}
                    disabled={isAccepting}
                  >
                    {isAccepting ? t('accepting') : t('accept_invite')}
                  </Button>
                </div>
              )}
            </section>
          ) : (
            <div className="text-center">
              <h2 className="text-lg font-medium">{t('no_invite')}</h2>
              <p className="text-sm text-muted-foreground mt-2">{t('no_invite_description')}</p>
            </div>
          )}
        </>
      )}
    </LayoutOutside>
  );
}
